import { useEffect, useState } from 'preact/hooks';
import { Link, useLocation } from 'wouter-preact';
import { api } from '@/lib/teaching/teacher-api';
import { useAssessmentId, teachHref } from '@/lib/teaching/state';
import { errText } from '@/lib/teaching/slice2';
import { BANDS, type Descriptors as DescriptorsView, type DescriptorRow } from '@/lib/teaching/slice3';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { SetupPage, Note, textareaCls, selectCls } from '@/components/teaching/setup';

// Criterion descriptors (§02): the teacher pastes the MYP level bands per
// strand once per test, or copies them from another test of the same
// criterion and year. Every save is a new version; judging needs one.

/** Descriptors / Judgements / Feedback, one rail entry (rail height). */
export function MypTabs({ a }: { a: string }) {
  const [loc] = useLocation();
  const tabs = [['/setup/descriptors', 'MYP descriptors'], ['/setup/judgements', 'MYP levels'], ['/setup/feedback', 'Feedback']] as const;
  return (
    <nav aria-label="MYP steps" class="inline-flex flex-wrap rounded-xl border border-[var(--color-border)] p-1 bg-[var(--color-card)] max-w-full">
      {tabs.map(([path, label]) => {
        const on = loc.startsWith(path);
        return (
          <Link key={path} href={teachHref(path, { a })} aria-current={on ? 'page' : undefined}
            class={'px-3 py-1 rounded-lg text-[12.5px] font-medium ' + (on ? 'bg-[var(--color-accent)]' : 'text-[var(--color-text-muted)]')}
            style={on ? { color: '#ffffff' } : undefined}>{label}</Link>
        );
      })}
    </nav>
  );
}

const key = (band: string, strand: string) => `${band}|${strand}`;

export function Descriptors() {
  const a = useAssessmentId();
  const [d, setD] = useState<DescriptorsView | null>(null);
  const [text, setText] = useState<Record<string, string>>({});
  const [strands, setStrands] = useState<string[]>([]);
  const [newStrand, setNewStrand] = useState('');
  const [copyId, setCopyId] = useState('');
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fill = (v: DescriptorsView, rows: DescriptorRow[] = v.rows) => {
    setText(Object.fromEntries(rows.map((r) => [key(r.band, r.strand), r.text])));
    setStrands(Array.from(new Set([...v.strands, ...rows.map((r) => r.strand)])).sort());
  };
  const load = async () => {
    if (!a) return;
    const v = await api<DescriptorsView>('GET', `/api/t/assessments/${a}/descriptors`);
    setD(v); fill(v);
  };
  useEffect(() => { setD(null); setErr(null); setMsg(null); void load().catch((e) => setErr(errText(e))); }, [a]);

  const rows = strands.flatMap((s) => BANDS.map((b) => ({ band: b, strand: s, text: (text[key(b, s)] ?? '').trim() }))).filter((r) => r.text);
  const dirty = d ? JSON.stringify(rows) !== JSON.stringify(strands.flatMap((s) => BANDS.map((b) => d.rows.find((r) => r.band === b && r.strand === s))).filter(Boolean).map((r) => ({ band: r!.band, strand: r!.strand, text: r!.text }))) : false;

  async function save() {
    if (!a || !d) return;
    setErr(null); setMsg(null); setBusy(true);
    try {
      const v = await api<DescriptorsView>('POST', `/api/t/assessments/${a}/descriptors`, copiedFrom ? { expectedVersion: d.current_version, copyFrom: copiedFrom } : { expectedVersion: d.current_version, rows });
      setD(v); fill(v); setCopiedFrom(null); setMsg(`Saved as version ${v.current_version}.`);
    } catch (e) { setErr(errText(e)); }
    setBusy(false);
  }
  function copy() {
    const src = d?.copyable.find((c) => c.id === copyId);
    if (src && d) { fill(d, src.rows); setCopiedFrom(src.id); setMsg(`Copied from ${src.title}. Check, then save.`); }
  }

  return (
    <SetupPage a={a} title="MYP descriptors" actions={a && <MypTabs a={a} />}>
      <ErrorLine error={err} />
      {!d && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {d && (
        <>
          <Note>
            Paste the MYP Criterion {d.criterion} level bands for Year {d.year_level}, one box per band and strand. The app does not ship IB descriptor text.
            The AI uses them for a best-fit recommendation; you confirm every level.
          </Note>
          {d.locked && <Note tone="warn">Criterion and year level are locked for this test because judging has started. Saving a new version sends confirmed levels and approved feedback back for re-checking.</Note>}
          <Card title={d.current_version ? `Version ${d.current_version}` : 'No descriptors yet'} sub={d.head ? `Saved by ${d.head.created_by}${d.head.copied_from ? ' (copied from another test)' : ''}` : 'Judging is blocked until descriptors are saved'}>
            {d.copyable.length > 0 && (
              <div class="flex flex-wrap items-end gap-2 mb-3">
                <label class="flex flex-col gap-1 text-[12px] text-[var(--color-text-muted)] min-w-0">
                  Copy from another test (Criterion {d.criterion}, Year {d.year_level})
                  <select class={selectCls} value={copyId} onChange={(e) => setCopyId(val(e))} aria-label="Copy from">
                    <option value="">Choose a test</option>
                    {d.copyable.map((c) => <option key={c.id} value={c.id}>{c.title} (v{c.version})</option>)}
                  </select>
                </label>
                <Btn disabled={!copyId} onClick={copy}>Copy</Btn>
              </div>
            )}
            <div class="flex flex-col gap-4">
              {strands.map((s) => (
                <div key={s} class="flex flex-col gap-2">
                  <div class="text-[12px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">Strand {s}</div>
                  <div class="grid sm:grid-cols-2 gap-2">
                    {BANDS.map((b) => (
                      <label key={b} class="flex flex-col gap-1 text-[12px] text-[var(--color-text-muted)] min-w-0">
                        Band {b}
                        <textarea rows={3} class={textareaCls} aria-label={`Strand ${s} band ${b}`} value={text[key(b, s)] ?? ''}
                          onInput={(e) => { setCopiedFrom(null); setText({ ...text, [key(b, s)]: (e.target as HTMLTextAreaElement).value }); }} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {strands.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">Tag questions with a strand on the test, or add one here.</div>}
              <div class="flex flex-wrap items-end gap-2">
                <label class="flex flex-col gap-1 text-[12px] text-[var(--color-text-muted)]">
                  Add a strand
                  <input class={inputCls + ' w-28'} value={newStrand} onInput={(e) => setNewStrand(val(e))} aria-label="New strand" placeholder="e.g. iii" />
                </label>
                <Btn disabled={!newStrand.trim() || strands.includes(newStrand.trim())} onClick={() => { setStrands([...strands, newStrand.trim()].sort()); setNewStrand(''); }}>Add strand</Btn>
              </div>
            </div>
            <div class="mt-4 flex flex-wrap items-center gap-2">
              <Btn kind="primary" disabled={busy || !rows.length || (!dirty && d.current_version > 0)} onClick={save}>Save descriptors</Btn>
              <span class="text-[12px] text-[var(--color-text-muted)]">{rows.length} band descriptor{rows.length === 1 ? '' : 's'}</span>
              {d.current_version > 0 && a && <Link href={teachHref('/setup/judgements', { a })} class="text-[13px] text-[var(--color-accent)]">Go to MYP levels</Link>}
            </div>
            {msg && <div class="mt-2"><Note tone="ok">{msg}</Note></div>}
          </Card>
        </>
      )}
    </SetupPage>
  );
}
